"use client";

import { GameThumbnail } from "@/components/feature/game-thumbnail";
import { PointerInteractionAutoProvider } from "@/components/feature/pointer-interaction-media";
import { PreviewIntentAutoProvider } from "@/components/feature/preview-intent";
import { useEdgeHoverTransform } from "@/hooks/useEdgeHoverTransform";
import { usePreviewIntentHandlers } from "@/hooks/usePreviewIntentHandlers";
import { type PageOriginContext, buildGameHref, writePageOrigin } from "@/lib/page-origin-context";
import type { Game } from "@/types/game";
import { GameSize } from "@/types/game";
import Link from "next/link";

/**
 * Game Size in Grid Requirements:
 * - Size 1: Takes up exactly 1×1 (1 column × 1 row) in the grid
 * - Size 2: Takes up exactly 2×2 (2 columns × 2 rows) in the grid
 * - Size 3: Takes up exactly 3×3 (3 columns × 3 rows) in the grid
 *
 * The size property in the Game object (game.size) controls this grid span.
 * This is different from the UI size prop below, which controls visual elements.
 */

interface GameCardProps {
  game: Game;
  size?: "small" | "medium" | "large"; // UI size (different from the game.size which determines grid span)
  /**
   * Optional override for preview identity. Use this when rendering duplicate instances of the
   * same game (e.g., looping rows) so that only the interacted instance becomes active.
   */
  previewIdOverride?: string;
  /** When set, game links preserve listing/chip context (games hub or topic page). */
  pageOrigin?: PageOriginContext | null;
  /** Set false for large transient grids (e.g. live search) to avoid prefetch storms. Default true. */
  linkPrefetch?: boolean;
  /** Called when the game link is activated (mouse click or Enter). */
  onLinkClick?: () => void;
}

function GameCardInner({
  game,
  size = "medium",
  previewIdOverride,
  pageOrigin,
  linkPrefetch = true,
  onLinkClick,
}: GameCardProps) {
  // Size-based classes for UI elements
  const newTagClasses = {
    small: "text-xs px-1.5 py-0",
    medium: "text-xs px-2 py-1",
    large: "text-sm px-3 py-1.5",
  };

  const nameClasses = {
    small: "px-1 pt-3 pb-2",
    medium: "px-1.5 pt-4 pb-2.5",
    large: "px-2 pt-5 pb-3",
  };

  const newTagScale: Record<GameSize, string> = {
    [GameSize.Small]: "scale-100",
    [GameSize.Medium]: "scale-125",
    [GameSize.Large]: "scale-150",
  };

  const imageSizes: Record<GameSize, string> = {
    [GameSize.Small]:
      "(max-width: 480px) 33vw, (max-width: 640px) 16vw, (max-width: 768px) 12vw, 8vw",
    [GameSize.Medium]:
      "(max-width: 480px) 67vw, (max-width: 640px) 33vw, (max-width: 768px) 25vw, 17vw",
    [GameSize.Large]:
      "(max-width: 480px) 67vw, (max-width: 640px) 50vw, (max-width: 768px) 38vw, 25vw",
  };

  const headingClasses: Record<GameSize, string> = {
    [GameSize.Small]: "text-[16px] md:text-[20px] font-medium leading-tight",
    [GameSize.Medium]: "text-[20px] md:text-[24px] font-semibold leading-tight",
    [GameSize.Large]: "text-[24px] md:text-[28px] font-bold leading-tight",
  };

  // Handle null game.size by defaulting to Small
  const gameSize = (game.size ?? GameSize.Small) as GameSize;
  const previewId = previewIdOverride ?? game.slug;
  const previewHandlers = usePreviewIntentHandlers({
    previewId,
    enabled: Boolean(game.preview_video_url),
  });
  const edgeHover = useEdgeHoverTransform();

  const href = buildGameHref(game.slug);

  return (
    <Link
      href={href}
      prefetch={linkPrefetch}
      className="block w-full h-full relative group"
      onClick={() => {
        writePageOrigin(pageOrigin);
        onLinkClick?.();
      }}
      onMouseEnter={previewHandlers.onMouseEnter}
      onMouseLeave={previewHandlers.onMouseLeave}
      onFocus={previewHandlers.onFocus}
      onBlur={previewHandlers.onBlur}
    >
      {/* Container with image and label - single rounded container */}
      <div
        ref={edgeHover.containerRef}
        className="w-full h-full relative rounded-[10%] overflow-hidden"
        {...edgeHover.containerHandlers}
      >
        {/* Image / Video Thumbnail */}
        <GameThumbnail
          previewId={previewId}
          imageUrl={game.thumbnail_url || "/placeholder.svg?height=300&width=300&query=game"}
          previewVideoUrl={game.preview_video_url}
          alt={game.name}
          imageClassName="object-cover"
          sizes={imageSizes[gameSize]}
        />

        {/* Game name overlay - appears on hover with bottom gradient scrim */}
        <div
          className={`absolute -bottom-px -left-px -right-px bg-gradient-to-t from-black/50 to-transparent
                     ${nameClasses[size]} text-center opacity-0 group-hover:opacity-100
                     transition-opacity duration-200 ease-in-out`}
        >
          <h3
            className={`text-white break-words [text-shadow:0_1px_2px_rgb(0_0_0/0.4),0_2px_8px_rgb(0_0_0/0.25)] ${headingClasses[gameSize]}`}
          >
            {game.name}
          </h3>
        </div>
      </div>

      {/* NEW tag - positioned to overlap the corner with hover animation */}
      {game.new_tag && (
        <div
          className={`absolute -top-2 -right-2 sm:-top-1 sm:-right-1 ${newTagClasses[size]} bg-red-500 text-white font-bold
                     rounded-full transform rotate-3 group-hover:rotate-6 z-10
                     transition-transform duration-200 ease-in-out border-2 border-white
                     ${newTagScale[gameSize]}`}
        >
          NEW!
        </div>
      )}
    </Link>
  );
}

export function GameCard(props: GameCardProps) {
  /**
   * Note: When rendering many `GameCard`s, prefer wrapping the whole list/grid in a single
   * `PreviewIntentProvider`/`PreviewIntentAutoProvider` so preview state is shared (only one active).
   * This component still includes `PreviewIntentAutoProvider` to remain safe when used standalone.
   */
  return (
    <PreviewIntentAutoProvider>
      <PointerInteractionAutoProvider>
        <GameCardInner {...props} />
      </PointerInteractionAutoProvider>
    </PreviewIntentAutoProvider>
  );
}
